import { useEffect, useMemo, useRef, useState } from "react";
import { makeStyles } from "@mui/styles";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import { Dialog, DialogContent, DialogTitle } from "@mui/material";
import {
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    CircularProgress
} from "@mui/material";

// import CircularProgress from "@material-ui/core/CircularProgress";

const SEARCH_DEBOUNCE_MS = 400;

const styles = {
    root: {
        backgroundColor: "#083663",
        borderTopLeftRadius: 5,
        borderTopRightRadius: 5
    },
    header: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between"
    },
    searchField: {
        margin: "unset",
        marginBottom: 16,
        width: "100%",
        "& input": {
            padding: "10px 14px"
        }
    },
    row: {
        cursor: "pointer",
        "&:hover": {
            background: "#f5f5f5"
        }
    },
    fields: {
        color: "#fff !important"
    },
    loadingCell: {
        textAlign: "center",
        padding: 24
    }
};

const useStyles = makeStyles(styles);

export default function LookupModal({
    resource,
    displayField,
    valueField,
    onSelect,
    onClose,
    filterValues
}) {
    const classes = useStyles();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    console.log({ filterValues });

    useEffect(() => {
        const controller = new AbortController();

        (async () => {
            setLoading(true);
            try {
                const response = await resource.service.search(undefined, {
                    signal: controller.signal
                });

                let items = response.data;
                if (filterValues) {
                    items = filterValues(items);
                }
                setItems(items);
            } catch (err) {
                if (
                    err.name !== "CanceledError" &&
                    err.code !== "ERR_CANCELED"
                ) {
                    throw err;
                }
            } finally {
                if (!controller.signal.aborted) {
                    setLoading(false);
                }
            }
        })();

        return () => {
            controller.abort();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const searchFields = resource.searchFields?.length
        ? resource.searchFields
        : [displayField];

    const filteredItems = useMemo(() => {
        if (!search.trim()) return items;

        const term = search.trim().toLowerCase();

        return items.filter((item) =>
            searchFields.some((field) =>
                String(item[field] ?? "")
                    .toLowerCase()
                    .includes(term)
            )
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items, search]);

    /*    const fetchItems = async (params = {}) => {
            setLoading(true);
            try {
                const response = await resource.service.search(params);
                setItems(response.data);
            } finally {
                setLoading(false);
            }
        };*/

    return (
        <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle className={classes.header}>
                Select {resource.title}
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogTitle className={classes.header}>
                <TextField
                    className={classes.searchField}
                    variant="outlined"
                    autoFocus
                    placeholder={`Search ${resource.displayField}`}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            )
                        }
                    }}
                />
            </DialogTitle>
            <DialogContent>
                <Table size="small">
                    <TableHead className={classes.root}>
                        <TableRow>
                            {resource.columns.map((col) => (
                                <TableCell
                                    key={col.field}
                                    className={classes.fields}
                                >
                                    {col.header}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell
                                    colSpan={resource.columns.length}
                                    className={classes.loadingCell}
                                >
                                    <CircularProgress size={24} />
                                </TableCell>
                            </TableRow>
                        ) : filteredItems.length === 0 ? (
                            <TableRow>
                                <TableCell
                                    colSpan={resource.columns.length}
                                    className={classes.loadingCell}
                                >
                                    No results
                                </TableCell>
                            </TableRow>
                        ) : (
                            filteredItems.map((item) => (
                                <TableRow
                                    key={item[valueField]}
                                    className={classes.row}
                                    onClick={() => onSelect(item)}
                                >
                                    {resource.columns.map((col) => (
                                        <TableCell key={col.field}>
                                            {item[col.field]}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </DialogContent>
        </Dialog>
    );
}
